import BaseChosen, { OpenerSlotProps } from '@Ravnur-Inc/core/components/base-chosen/base-chosen';
import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Prop, Debounce } from '@Ravnur-Inc/decorators';
import { ErrorObject } from '@vuelidate/core';
import { Vue, Options } from 'vue-class-component';

import { SEARCH_DEBOUNCE_TIME } from '@/constants';
import LookupRepository from '@/repositories/lookup-repository';
import TextField from '@Ravnur-Inc/core/ui-kit/text-field/text-field';
import BaseRepository from '@Ravnur-Inc/http/base-repository';

import './lookup.scss';

const CN = 'lookup';
const ENTER_KEYCODE = 13;

type Mode = 'single' | 'multi';

type Props<E extends Entity> = {
  value?: Nullable<E>;
  label?: string;
  placeholder?: string;
  propName?: keyof SubType<E, string>;
  repository: LookupRepository<E>;
  creator?: boolean;
  mode?: 'single' | 'multi';
  errors?: ErrorObject[];
  queryParams?: Dictionary<unknown>;
};

type Emits<E> = {
  onSelect: (entity: E) => void;
};

@Options({
  name: 'lookup',
  emits: ['select'],
  watch: {
    value(this: Lookup<Entity>) {
      this.handleValueChanged();
    },
  },
})
export default class Lookup<E extends Entity> extends Vue {
  declare $props: $Types<Props<E>, Emits<E>, 'value'> &
    Partial<Pick<HTMLInputElement, 'maxLength'>>;

  declare $refs: {
    input: TextField;
  };

  @Prop({ type: Object }) public value!: Nullable<E>;
  @Prop({ type: String, default: '' }) public label!: string;
  @Prop({ type: String, default: '' }) public placeholder!: string;
  @Prop({ type: String, default: 'name' })
  public propName!: E extends Dictionary<unknown> ? keyof E : never;
  @Prop({ type: Object, required: true }) public repository!: LookupRepository<E>;
  @Prop({ type: Object, required: true }) public createRepository?: BaseRepository<E>;
  @Prop({ type: Boolean, default: true }) public creator!: boolean;
  @Prop({ type: Boolean, default: false }) public requiredAsterisk?: boolean;
  @Prop({ type: String, default: 'multi' }) public mode!: Mode;
  @Prop({ type: Object }) public readonly errors: ErrorObject[];
  @Prop({ type: Object, default: () => ({}) }) public queryParams!: Dictionary<unknown>;

  private q = '';
  private loading = false;
  private items: E[] = [];

  private get isSingleMode() {
    return this.mode === 'single';
  }

  protected handleValueChanged() {
    const value = this.value?.[this.propName];
    this.q = value?.toString() || '';
  }

  render() {
    return (
      <BaseChosen
        class={CN}
        current={null}
        keyForLabel={this.propName}
        options={this.items}
        v-slots={{ button: this.renderSearchField, ...this.$slots }}
        onInput={this.select}
      />
    );
  }

  private renderSearchField({ toggle }: OpenerSlotProps) {
    return (
      <text-field
        ref="input"
        class={`${CN}__field`}
        errors={this.errors}
        icon="search"
        label={this.label}
        onfocus={toggle}
        oninput={this.handleQChanged}
        onkeydown={this.handleKeydown}
        placeholder={this.placeholder}
        requiredAsterisk={this.requiredAsterisk}
        vModel={[this.q, 'value']}
        {...this.$attrs}
        v-slots={{ actions: this.renderSpinner }}
      />
    );
  }

  private renderSpinner() {
    if (!this.loading) {
      return;
    }
    return <spinner class={`${CN}__spinner`} size="sm" think={true} />;
  }

  private handleQChanged() {
    this.$nextTick(this.load);
  }

  @Debounce(SEARCH_DEBOUNCE_TIME)
  private async load() {
    const { q, propName: prop, isSingleMode, queryParams } = this;

    if (!q) {
      this.items.length = 0;
      return;
    }

    this.loading = true;

    try {
      if (isSingleMode) {
        this.$emit('select', { [prop]: q });
      }
      const params = { ...queryParams, q, count: 15, offset: 0 };
      const { items } = await this.repository.load(params);

      this.items = items;
      this.$nextTick(this.$refs.input.focus);
    } catch (e) {
      this.$logger.error(e);
    } finally {
      this.loading = false;
    }
  }

  private select(entity: E) {
    const { isSingleMode, propName } = this;
    if (isSingleMode) {
      this.q = entity[propName];
    } else {
      this.q = '';
    }
    this.items.length = 0;
    this.$emit('select', entity);
  }

  private async createEntityAndSelect() {
    const { items, propName, q, repository: lookupRepository, createRepository } = this;

    if (!q) {
      return;
    }

    let repository: BaseRepository<E> | LookupRepository<E> = lookupRepository;

    if (createRepository) {
      repository = createRepository;
    }

    const first = items.find((i) => i[propName] === q);
    if (first) {
      this.select(first);
      return;
    }
    this.loading = true;
    const entity: Partial<E> = {};
    entity[propName] = q;
    try {
      const op = await repository.save(entity);

      if (op && (op as any).entityId) {
        const saved = await repository.get((op as any).entityId);
        this.select(saved);
      }
    } catch (e) {
      this.$processException(e);
    } finally {
      this.loading = false;
    }
  }

  private handleKeydown(e: KeyboardEvent) {
    if (!this.loading && this.creator && e.which === ENTER_KEYCODE) {
      this.createEntityAndSelect();
    }
  }
}
